import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import FullScreenDialog from "@/components/FullScreenDialog";
import Ref from "@/components/Ref";
import { ExternalLink, FileText, FolderTree } from "lucide-react";
import { fmtDate } from "@/lib/dt";
import { wsHref } from "@/lib/worksheetLinks";
import { asArray } from "@/lib/asArray";
import { SURL_CODE_RX, TXT as SURL_TXT } from "@/components/SurlWorksheetDialog";   // Batch 26 · Add-on 1

/**
 * Round 8 combined · W1 — Forms & worksheets list dialog converted
 * to the shared FullScreenDialog shell. Esc + Close + return-to-origin
 * inherited from the shell. D5: shows created_by · date when the
 * server attached it to the pinned row.
 */
const STATIC_WS = {
  "1203-06": { kind: "aging", en: "AR aging", ar: "أعمار الذمم المدينة" },
  "3203-06": { kind: "aging", en: "AP aging", ar: "أعمار الذمم الدائنة" },
  "4101-06": { kind: "cutoff", en: "Cut-off test — Revenue", ar: "اختبار فصل الفترات — الإيرادات" },
  "5101-06": { kind: "cutoff", en: "Cut-off test — Cost of sales", ar: "اختبار فصل الفترات — تكلفة المبيعات" },
  "1201-07": { kind: "cutoff", en: "Cut-off test — Inventories", ar: "اختبار فصل الفترات — المخزون" },
};

export default function HeadFolderFormsListDialog({ open, onClose,
                                                        fsCode, fileId, heads, engId, onChanged }) {
  const { t, lang } = useI18n();
  const [busy, setBusy] = useState(false);
  const hf = asArray(heads).find((h) => h.fs_code === fsCode);
  const file = asArray(hf?.files).find((f) => f.id === fileId);
  const slotRef = `${fsCode}-${fileId}`;
  const staticWs = STATIC_WS[slotRef];
  // Round 33 · A4 — the slot's own aging / cut-off worksheet is listed here
  // (the backend de-dupes it out of pinned_forms), so "N files" = rows shown.
  const slotRow = (file?.has_pinned_worksheet && staticWs)
    ? [{ worksheet_code: slotRef, ref_no: slotRef, worksheet_kind: staticWs.kind,
         title: staticWs.en, title_ar: staticWs.ar, _slot: true }] : [];
  // Batch 26 · Add-on 1 — every liability head offers "Search for unrecorded liabilities" (<head>-07); its slot row is listed here
  const surlCode = `${fsCode}-07`;
  const isLiability = SURL_CODE_RX.test(surlCode);
  const surlFile = asArray(hf?.files).find((f) => f.id === "07");
  const hasSurl = !!(surlFile?.has_pinned_worksheet && surlFile?.pinned_worksheet_kind === "surl");
  const surlRow = (isLiability && hasSurl && fileId === "07")
    ? [{ worksheet_code: surlCode, ref_no: surlCode, worksheet_kind: "surl", title: SURL_TXT.en.title, title_ar: SURL_TXT.ar.title, _slot: true }] : [];
  const canAddSurl = isLiability && !hasSurl && (fileId === "06" || fileId === "07") && !!engId;
  const addSurl = async () => {
    setBusy(true);
    try { await api.post(`/engagements/${engId}/surl/${surlCode}/add`); toast.success(t("hfWs.added") || "Worksheet added"); onChanged?.(); }
    catch (e) { toast.error(toErrMsg(e)); } finally { setBusy(false); }
  };
  const pinned = [...slotRow, ...surlRow, ...(file?.pinned_forms || [])];
  const canAdd = !!staticWs && !file?.has_pinned_worksheet && !!engId;
  const addWorksheet = async () => {
    setBusy(true);
    try {
      await api.post(`/engagements/${engId}/head-folder-worksheets/${slotRef}/add`);
      toast.success(t("hfWs.added") || "Worksheet added");
      onChanged?.();
    } catch (e) { toast.error(toErrMsg(e)); } finally { setBusy(false); }
  };
  const title = fileId === "06"
    ? (t("headFolders.formsListTitle") || "Forms & worksheets")
    : (t("headFolders.indexListTitle") || "Index");

  return (
    <FullScreenDialog open={!!open} onClose={onClose}
                         testid="hf-forms-list-dialog" title={title}
                         titleBadge={<>
                           <FolderTree className="h-4 w-4 text-emerald-600" />
                           <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-mono text-emerald-800">
                             <Ref>{`${fsCode}-${fileId}`}</Ref>
                           </span>
                           <span className="text-xs text-muted-foreground">
                             {pinned.length} {t("headFolders.filesSuffix") || "files"}
                           </span>
                         </>}>
      {canAdd && (
        <button type="button" onClick={addWorksheet} disabled={busy}
                className="mb-3 rounded border border-emerald-600 px-3 py-1.5 text-sm text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                data-testid={`hf-add-worksheet-${slotRef}`}>
          {(t("hfWs.addWorksheet") || "Add worksheet")}: {staticWs.kind === "aging" ? (t("hfWs.row.aging") || "Aging") : (t("hfWs.row.cutoff") || "Cut-off")} · <Ref>{slotRef}</Ref>
        </button>
      )}
      {canAddSurl && (
        <button type="button" onClick={addSurl} disabled={busy}
                className="mb-3 ms-2 rounded border border-emerald-600 px-3 py-1.5 text-sm text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                data-testid={`hf-add-surl-${surlCode}`}>
          {(t("hfWs.addWorksheet") || "Add worksheet")}: {SURL_TXT[lang === "ar" ? "ar" : "en"].title} · <Ref>{surlCode}</Ref>
        </button>
      )}
      {pinned.length === 0 ? (
        <div className="rounded border p-6 text-center text-sm text-muted-foreground"
             data-testid="hf-forms-list-empty">
          {t("headFolders.formsEmptyHint") || "No forms or worksheets yet in this folder."}
        </div>
      ) : (
        <ul className="divide-y rounded border" data-testid="hf-forms-list">
          {pinned.map((p) => {
            const isWs = !!p.worksheet_code;
            const ref = p.ref_no || p.worksheet_code || `${fsCode}-${fileId}`;
            const rowTitle = p.title_ar && lang === "ar" ? p.title_ar : p.title;
            const to = isWs
              ? wsHref(p.worksheet_kind, ref)        // Batch 11 · B2: one table for the dialog parameters
              : `/planning?openForm=${encodeURIComponent(p.form_id || "")}`;
            return (
              <li key={`${ref}-${p.form_id || p.worksheet_code}`}
                  data-testid={`hf-forms-list-row-${ref}`}>
                <Link to={to} onClick={onClose}
                      className="flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-emerald-50 dark:hover:bg-emerald-950/20">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-mono font-semibold ${isWs ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-800"}`}>
                      <Ref>{ref}</Ref>
                    </span>
                    <span className="break-words">{rowTitle}</span>
                    {p.completed && (
                      <span className="ms-1 rounded bg-emerald-100 px-1 py-0.5 text-[9px] font-medium text-emerald-800">
                        {t("common.completed") || "Completed"}
                      </span>
                    )}
                    {p.has_listing && (
                      <span className="ms-1 rounded bg-sky-100 px-1 py-0.5 text-[9px] font-medium text-sky-800">
                        <span dir="ltr">{p.row_count}</span> {t("common.rows") || "rows"}
                      </span>
                    )}
                    {(p.created_by_name || p.created_at) && (
                      <span className="ms-2 text-[10px] text-muted-foreground"
                            data-testid={`hf-row-created-${ref}`}>
                        · {p.created_by_name || "—"}
                        {p.created_at ? ` · ${fmtDate(p.created_at)}` : ""}
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-2 text-xs text-muted-foreground">
                    <FileText className="h-3 w-3" />
                    <ExternalLink className="h-3 w-3 text-emerald-600" />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </FullScreenDialog>
  );
}
